import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import DateInput from '@/components/DateInput'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import WarningBanner from '@/components/WarningBanner'
import { SectionCard } from '@/components/SectionCard'
import LaborStoreCompare from '@/components/payroll/LaborStoreCompare'
import LaborQueryButton from '@/components/payroll/LaborQueryButton'
import { incompleteRolesWarningMessage, stationNamesFromRoleMapping } from '@/components/payroll/paymasterSettings'
import {
  laborQueryButtonLabel,
  laborQueryShowsCacheMenu,
  laborRangeForPreset,
  type LaborRangePreset,
} from '@/components/payroll/laborAnalyticsControls'
import {
  buildLaborAnalyticsExportFilename,
  buildLaborAnalyticsExportMatrix,
  collectLaborExportPayloads,
} from '@/components/payroll/laborAnalyticsExport'
import {
  aggregateChartsByStore,
  buildStoreClosedDates,
  convertHoursToPercentSeries,
  filterChartsByStoreByStations,
  formatHoursAxisPct,
  formatHoursTooltip,
  formatHoursTooltipPct,
  formatLaborAxisPct,
  formatLaborTooltipPct,
  laborSeriesHasValues,
  parseLaborValue,
  type SectionLoadState,
  sectionStateFromResult,
  withRatioNumeratorDenominator,
  withoutTotalSeries,
} from '@/components/payroll/laborChartSeries'
import { formatAxisUsd, formatUsd } from '@/components/gcp/gcpHelpers'
import { getSettings } from '@/services/settings'
import {
  exportLaborAnalyticsSheet,
  fetchLaborRefresh,
  LABOR_STORE_NAMES,
  LABOR_TIMESCALES,
  selectedLaborNames,
  type LaborChartMetric,
  type LaborChartResponse,
  type LaborRefreshResponse,
  type LaborSeries,
  type LaborStoreName,
  type LaborTimescale,
  type WeeklyOtEmployeeResponse,
  type WeeklyOtEmployeeRow,
  type WeeklyOtStationResponse,
  type WeeklyOtStationRow,
} from '@/services/laborAnalytics'
import { formatApiError } from '@/utils/apiError'
import { downloadCsvFile, escapeCsvCell } from '@/utils/warehouseTableHelpers'
import { toast } from '@/store/toastStore'
import { TableToolbar } from '@/tableControls'
import {
  clearLaborAnalyticsCache,
  hasLaborAnalyticsRangeCache,
  loadLaborAnalyticsCacheEntry,
  loadLatestLaborAnalyticsCache,
  saveLaborAnalyticsCache,
  type LaborAnalyticsCacheEntry,
} from '@/utils/laborAnalyticsCache'
import {
  formatWeekEndedLabel,
  LABOR_STORE_PANE_GRID_CLASS,
  maxPositive,
  otHeatBackground,
  weeksEndingInRange,
} from '@/components/payroll/laborOtTables'
import {
  laborChartCaption,
  WAGES_STATION_SALES_CAVEAT,
} from '@/components/payroll/laborAnalyticsCaptions'

const RANGE_PRESETS: Array<{ value: LaborRangePreset; label: string }> = [
  { value: 'last13weeks', label: 'Last 13 weeks' },
  { value: 'ytd', label: 'YTD' },
  { value: 'ltm', label: 'LTM' },
  { value: 'custom', label: 'Custom' },
]

const DEFAULT_RANGE = laborRangeForPreset('last13weeks')

function storeMetricSeries(
  chartsByStore: LaborRefreshResponse['charts_by_store'] | undefined,
  metric: LaborChartMetric,
): Record<LaborStoreName, LaborSeries[]> {
  const result = {} as Record<LaborStoreName, LaborSeries[]>
  for (const store of LABOR_STORE_NAMES) {
    result[store] = chartsByStore?.[store]?.[metric]?.series ?? []
  }
  return result
}

function formatHours(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatAxisHours(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (value: T) => void
  ariaLabel: string
}) {
  return (
    <div
      className="inline-flex rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
            value === opt.value
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function chartIsEmpty(response: LaborChartResponse | undefined): boolean {
  return response == null || !laborSeriesHasValues(response.series ?? [])
}

function chartsByStoreMetricEmpty(
  chartsByStore: LaborRefreshResponse['charts_by_store'],
  metric: LaborChartMetric,
): boolean {
  if (!chartsByStore) return true
  return LABOR_STORE_NAMES.every((store) => chartIsEmpty(chartsByStore[store]?.[metric]))
}

function emptyChart(metric: LaborChartMetric, store = LABOR_STORE_NAMES[0]): LaborChartResponse {
  return {
    metric,
    store,
    timescale: 'L1',
    start: '',
    end: '',
    series: [],
  }
}

function firstStoreChart(
  chartsByStore: LaborRefreshResponse['charts_by_store'],
  metric: LaborChartMetric,
): LaborChartResponse {
  for (const store of LABOR_STORE_NAMES) {
    const chart = chartsByStore?.[store]?.[metric]
    if (chart) return chart
  }
  return emptyChart(metric)
}

function displayPayloadFromCache(
  payload: LaborRefreshResponse,
  timescale: LaborTimescale,
  displayStart: string,
  displayEnd: string,
): LaborRefreshResponse {
  if (!payload.charts_by_store) return payload
  return {
    ...payload,
    charts_by_store: aggregateChartsByStore(
      payload.charts_by_store,
      timescale,
      displayStart,
      displayEnd,
    ),
  }
}

function compareLoadingOr(
  chartsByStore: LaborRefreshResponse['charts_by_store'] | undefined,
  render: () => ReactNode,
): ReactNode {
  if (!chartsByStore) {
    return <p className="text-sm text-gray-500">Loading…</p>
  }
  return render()
}

function otEmployeeIsEmpty(response: WeeklyOtEmployeeResponse | undefined): boolean {
  return response == null || response.rows.length === 0
}

function otStationIsEmpty(response: WeeklyOtStationResponse | undefined): boolean {
  return response == null || response.rows.length === 0
}

function SectionBody<T>({
  state,
  render,
  fill = false,
}: {
  state: SectionLoadState<T>
  render: (data: T) => ReactNode
  fill?: boolean
}) {
  if (state.status === 'idle') {
    return <p className="text-sm text-gray-500">Choose a range and click Run query.</p>
  }
  if (state.status === 'loading') {
    return <p className="text-sm text-gray-500">Loading…</p>
  }
  if (state.status === 'error') {
    return <p className="text-sm text-red-600">{state.message}</p>
  }
  if (state.status === 'empty') {
    return <p className="text-sm text-gray-500">No data for this range.</p>
  }
  if (!fill) return <>{render(state.data)}</>
  return <div className="flex min-h-0 flex-1 flex-col">{render(state.data)}</div>
}

function storesToRender(storeFilter: string[]): LaborStoreName[] {
  if (storeFilter.length === 0) return [...LABOR_STORE_NAMES]
  return LABOR_STORE_NAMES.filter((name) => storeFilter.includes(name))
}

function OtWeekSelect({
  weeks,
  value,
  onChange,
  rangeStart,
  rangeEnd,
}: {
  weeks: string[]
  value: string | null
  onChange: (week: string) => void
  rangeStart: string
  rangeEnd: string
}) {
  if (weeks.length === 0) return null
  return (
    <label className="text-xs text-gray-600">
      Week
      <select
        className="input mt-1 block min-w-[12rem] text-sm"
        value={value ?? weeks[0]}
        onChange={(event) => onChange(event.target.value)}
      >
        {weeks.map((week) => (
          <option key={week} value={week}>
            {formatWeekEndedLabel(week, rangeStart, rangeEnd)}
          </option>
        ))}
      </select>
    </label>
  )
}

function employeeHeatValue(row: WeeklyOtEmployeeRow): number {
  if (row.bucket === 'all_other') {
    return parseLaborValue(row.avg_ot_per_employee)
  }
  return parseLaborValue(row.ot_hours)
}

function WeeklyOtEmployeeTable({
  data,
  week,
  storeFilter,
}: {
  data: WeeklyOtEmployeeResponse
  week: string | null
  storeFilter: string[]
}) {
  const storeMax = useMemo(() => {
    const byStore = new Map<string, number>()
    for (const storeName of storesToRender(storeFilter)) {
      byStore.set(
        storeName,
        maxPositive(
          data.rows.filter((row) => row.store === storeName).map(employeeHeatValue),
        ),
      )
    }
    return byStore
  }, [data.rows, storeFilter])

  if (!week) {
    return <p className="text-sm text-gray-500">No Saturday week-ends in this range.</p>
  }

  return (
    <div className={LABOR_STORE_PANE_GRID_CLASS}>
      {storesToRender(storeFilter).map((storeName) => {
        const rows = data.rows.filter(
          (row) => row.store === storeName && row.week_ended === week,
        )
        const max = storeMax.get(storeName) ?? 0
        return (
          <div
            key={storeName}
            className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200"
          >
            <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
              {storeName}
            </p>
            {rows.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-500">No overtime this week.</p>
            ) : (
              <table className="table-forecast min-w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Employee</th>
                    <th>Role</th>
                    <th className="text-right">OT hours</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const heat = otHeatBackground(employeeHeatValue(row), max)
                    if (row.bucket === 'all_other') {
                      return (
                        <tr key={`${week}-${storeName}-other`}>
                          <td>—</td>
                          <td>All other employees w/ OT ({row.other_count ?? 0})</td>
                          <td>—</td>
                          <td className="text-right" style={heat ? { backgroundColor: heat } : undefined}>
                            {formatHours(parseLaborValue(row.ot_hours))}
                            {row.avg_ot_per_employee ? (
                              <span className="block text-[11px] text-gray-500">
                                avg {formatHours(parseLaborValue(row.avg_ot_per_employee))} h
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      )
                    }
                    return (
                      <tr key={`${week}-${storeName}-${row.employee_id ?? index}`}>
                        <td>{row.rank ?? '—'}</td>
                        <td>
                          {[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td>{row.role || '—'}</td>
                        <td className="text-right" style={heat ? { backgroundColor: heat } : undefined}>
                          {formatHours(parseLaborValue(row.ot_hours))}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

function WeeklyOtStationTable({
  data,
  week,
  storeFilter,
  stationFilter,
}: {
  data: WeeklyOtStationResponse
  week: string | null
  storeFilter: string[]
  stationFilter: string[] | null
}) {
  const storeMax = useMemo(() => {
    const byStore = new Map<string, number>()
    for (const storeName of storesToRender(storeFilter)) {
      byStore.set(
        storeName,
        maxPositive(
          data.rows
            .filter((row) => row.store === storeName)
            .map((row) => parseLaborValue(row.ot_hours)),
        ),
      )
    }
    return byStore
  }, [data.rows, storeFilter])

  if (!week) {
    return <p className="text-sm text-gray-500">No Saturday week-ends in this range.</p>
  }

  return (
    <div className={LABOR_STORE_PANE_GRID_CLASS}>
      {storesToRender(storeFilter).map((storeName) => {
        const rows = data.rows
          .filter((row) => row.store === storeName && row.week_ended === week)
          .filter((row) => stationFilter === null || stationFilter.includes(row.station))
          .sort((a, b) => parseLaborValue(b.ot_hours) - parseLaborValue(a.ot_hours))
        const max = storeMax.get(storeName) ?? 0
        return (
          <div
            key={storeName}
            className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200"
          >
            <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700">
              {storeName}
            </p>
            {rows.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-500">No overtime this week.</p>
            ) : (
              <table className="table-forecast min-w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th>Station</th>
                    <th className="text-right">OT hours</th>
                    <th className="text-right">FTE</th>
                    <th className="text-right">Avg OT / FTE</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: WeeklyOtStationRow) => {
                    const heat = otHeatBackground(parseLaborValue(row.ot_hours), max)
                    return (
                      <tr key={`${week}-${storeName}-${row.station}`}>
                        <td>{row.station}</td>
                        <td className="text-right" style={heat ? { backgroundColor: heat } : undefined}>
                          {formatHours(parseLaborValue(row.ot_hours))}
                        </td>
                        <td className="text-right">
                          {formatHours(parseLaborValue(row.fte))}
                        </td>
                        <td className="text-right">
                          {row.avg_ot_per_fte
                            ? formatHours(parseLaborValue(row.avg_ot_per_fte))
                            : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

function idleState<T>(): SectionLoadState<T> {
  return { status: 'idle' }
}

function statesFromPayload(payload: LaborRefreshResponse): {
  grossSalesPerHour: SectionLoadState<LaborChartResponse>
  regularHours: SectionLoadState<LaborChartResponse>
  overtimeHours: SectionLoadState<LaborChartResponse>
  wagesByStation: SectionLoadState<LaborChartResponse>
  wagesPctOfStationSales: SectionLoadState<LaborChartResponse>
  avgHourlyWages: SectionLoadState<LaborChartResponse>
  wagesPctOfSales: SectionLoadState<LaborChartResponse>
  otEmployees: SectionLoadState<WeeklyOtEmployeeResponse>
  otStations: SectionLoadState<WeeklyOtStationResponse>
} {
  const chartsByStore = payload.charts_by_store
  return {
    grossSalesPerHour: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'gross_sales_per_hour') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'gross_sales_per_hour'),
      'Failed to load Gross sales per hour',
    ),
    regularHours: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'regular_hours_by_station') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'regular_hours_by_station'),
      'Failed to load Regular hours by station',
    ),
    overtimeHours: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'overtime_hours_by_station') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'overtime_hours_by_station'),
      'Failed to load Overtime hours by station',
    ),
    wagesByStation: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'wages_by_station') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'wages_by_station'),
      'Failed to load Wages by station',
    ),
    wagesPctOfStationSales: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'wages_pct_of_station_sales') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'wages_pct_of_station_sales'),
      'Failed to load Wages percent of station sales',
    ),
    avgHourlyWages: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'avg_hourly_wages') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'avg_hourly_wages'),
      'Failed to load Average hourly wages',
    ),
    wagesPctOfSales: sectionStateFromResult(
      { status: 'fulfilled', value: firstStoreChart(chartsByStore, 'wages_pct_of_sales') },
      () => chartsByStoreMetricEmpty(chartsByStore, 'wages_pct_of_sales'),
      'Failed to load Wages as % of gross sales',
    ),
    otEmployees: sectionStateFromResult(
      { status: 'fulfilled', value: payload.overtime_employees },
      otEmployeeIsEmpty,
      'Failed to load overtime by employee',
    ),
    otStations: sectionStateFromResult(
      { status: 'fulfilled', value: payload.overtime_stations },
      otStationIsEmpty,
      'Failed to load overtime by station',
    ),
  }
}

function formatCachedAt(savedAt: number): string {
  return new Date(savedAt).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function LaborAnalyticsTab() {
  const boot = useMemo(() => loadLatestLaborAnalyticsCache(), [])
  const bootTimescale = boot?.query.timescale ?? 'L7'
  const bootDisplayPayload = boot
    ? displayPayloadFromCache(boot.payload, bootTimescale, boot.query.start, boot.query.end)
    : null
  const initialStates = bootDisplayPayload ? statesFromPayload(bootDisplayPayload) : null

  const [stations, setStations] = useState<string[]>(() =>
    selectedLaborNames(boot?.query.stations ?? boot?.query.station),
  )
  const [stationNames, setStationNames] = useState<string[]>([])
  const [roleStationNamesLoaded, setRoleStationNamesLoaded] = useState(false)
  const [chartsByStore, setChartsByStore] = useState<
    LaborRefreshResponse['charts_by_store'] | undefined
  >(bootDisplayPayload?.charts_by_store)
  const [regularHoursMode, setRegularHoursMode] = useState<'abs' | 'pct'>('abs')
  const [overtimeHoursMode, setOvertimeHoursMode] = useState<'abs' | 'pct'>('abs')
  const [wagesMode, setWagesMode] = useState<'usd' | 'pct'>('usd')
  const [rangePreset, setRangePreset] = useState<LaborRangePreset>(boot?.rangePreset ?? 'last13weeks')
  const [startDate, setStartDate] = useState(boot?.query.start ?? DEFAULT_RANGE.start)
  const [endDate, setEndDate] = useState(boot?.query.end ?? DEFAULT_RANGE.end)
  const [timescale, setTimescale] = useState<LaborTimescale>(boot?.query.timescale ?? 'L7')
  const [refreshing, setRefreshing] = useState(false)
  const [chartsRefreshing, setChartsRefreshing] = useState(false)
  const [hasRunQuery, setHasRunQuery] = useState(() => boot != null)
  const [isExporting, setIsExporting] = useState(false)
  const applyViewGenerationRef = useRef(0)
  const chartsByStoreRef = useRef(chartsByStore)
  chartsByStoreRef.current = chartsByStore
  const [cachedAt, setCachedAt] = useState<number | null>(boot?.savedAt ?? null)
  const [pulledRange, setPulledRange] = useState<{ start: string; end: string } | null>(
    boot ? { start: boot.query.start, end: boot.query.end } : null,
  )

  const [grossSalesPerHour, setGrossSalesPerHour] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.grossSalesPerHour ?? idleState(),
  )
  const [regularHours, setRegularHours] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.regularHours ?? idleState(),
  )
  const [overtimeHours, setOvertimeHours] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.overtimeHours ?? idleState(),
  )
  const [wagesByStation, setWagesByStation] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.wagesByStation ?? idleState(),
  )
  const [wagesPctOfStationSales, setWagesPctOfStationSales] = useState<
    SectionLoadState<LaborChartResponse>
  >(initialStates?.wagesPctOfStationSales ?? idleState())
  const [avgHourlyWages, setAvgHourlyWages] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.avgHourlyWages ?? idleState(),
  )
  const [wagesPctOfSales, setWagesPctOfSales] = useState<SectionLoadState<LaborChartResponse>>(
    initialStates?.wagesPctOfSales ?? idleState(),
  )
  const [otEmployees, setOtEmployees] = useState<SectionLoadState<WeeklyOtEmployeeResponse>>(
    initialStates?.otEmployees ?? idleState(),
  )
  const [otStations, setOtStations] = useState<SectionLoadState<WeeklyOtStationResponse>>(
    initialStates?.otStations ?? idleState(),
  )
  const [incompleteRoles, setIncompleteRoles] = useState<string[]>([])
  const [selectedOtWeek, setSelectedOtWeek] = useState<string | null>(null)

  const otWeeks = useMemo(() => weeksEndingInRange(startDate, endDate), [endDate, startDate])

  useEffect(() => {
    if (otWeeks.length === 0) {
      setSelectedOtWeek(null)
      return
    }
    setSelectedOtWeek((current) =>
      current && otWeeks.includes(current) ? current : (otWeeks[0] ?? null),
    )
  }, [otWeeks])

  useEffect(() => {
    void (async () => {
      try {
        const settings = await getSettings()
        const raw = settings.settings.labor_role_mapping?.value
        if (!raw?.trim()) {
          setStationNames([])
          return
        }
        const parsed = JSON.parse(raw) as Record<string, { station?: string }>
        setStationNames(stationNamesFromRoleMapping(parsed))
      } catch {
        /* Settings load is best-effort. */
      } finally {
        setRoleStationNamesLoaded(true)
      }
    })()
  }, [])

  useEffect(() => {
    if (stationNames.length === 0) return
    setStations((current) => current.filter((name) => stationNames.includes(name)))
  }, [stationNames])

  const applyCachedEntry = useCallback((entry: LaborAnalyticsCacheEntry) => {
    const displayPayload = displayPayloadFromCache(
      entry.payload,
      timescale,
      startDate,
      endDate,
    )
    const next = statesFromPayload(displayPayload)
    setGrossSalesPerHour(next.grossSalesPerHour)
    setRegularHours(next.regularHours)
    setOvertimeHours(next.overtimeHours)
    setWagesByStation(next.wagesByStation)
    setWagesPctOfStationSales(next.wagesPctOfStationSales)
    setAvgHourlyWages(next.avgHourlyWages)
    setWagesPctOfSales(next.wagesPctOfSales)
    setOtEmployees(next.otEmployees)
    setOtStations(next.otStations)
    setIncompleteRoles(entry.payload.incomplete_roles ?? [])
    setChartsByStore(displayPayload.charts_by_store)
    setCachedAt(entry.savedAt)
    if (entry.query.start === startDate && entry.query.end === endDate) {
      setHasRunQuery(true)
    }
  }, [endDate, startDate, timescale])

  const resetToIdle = useCallback(() => {
    applyViewGenerationRef.current += 1
    setChartsRefreshing(false)
    setGrossSalesPerHour(idleState())
    setRegularHours(idleState())
    setOvertimeHours(idleState())
    setWagesByStation(idleState())
    setWagesPctOfStationSales(idleState())
    setAvgHourlyWages(idleState())
    setWagesPctOfSales(idleState())
    setOtEmployees(idleState())
    setOtStations(idleState())
    setIncompleteRoles([])
    setChartsByStore(undefined)
    setCachedAt(null)
  }, [])

  const rangeCacheQuery = useMemo(
    () => ({
      stations: [] as string[],
      start: startDate,
      end: endDate,
    }),
    [endDate, startDate],
  )

  const fetchQuery = useMemo(
    () => ({
      ...rangeCacheQuery,
      timescale: 'L1' as const,
    }),
    [rangeCacheQuery],
  )

  const stationAllowlist = roleStationNamesLoaded ? stationNames : null

  const effectiveStationFilter = useMemo((): string[] | null => {
    if (!roleStationNamesLoaded) return null
    if (stations.length > 0) return stations
    return stationNames
  }, [roleStationNamesLoaded, stationNames, stations])

  const filteredChartsByStore = useMemo(
    () => filterChartsByStoreByStations(chartsByStore, stations, stationAllowlist),
    [chartsByStore, stationAllowlist, stations],
  )

  const closedDatesByStore = useMemo(() => {
    if (!chartsByStore) {
      return {} as Record<LaborStoreName, ReadonlySet<string>>
    }
    // Use unfiltered API charts: store-closed is whole-store sales+hours, not station-filtered totals.
    return Object.fromEntries(
      LABOR_STORE_NAMES.map((store) => {
        const charts = chartsByStore[store]
        return [store, charts ? buildStoreClosedDates(charts) : new Set<string>()]
      }),
    ) as Record<LaborStoreName, ReadonlySet<string>>
  }, [chartsByStore])

  const stationOptions = useMemo(
    () =>
      [...stationNames]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ id: name, label: name })),
    [stationNames],
  )

  const hasPulledCurrentRange =
    pulledRange != null && pulledRange.start === startDate && pulledRange.end === endDate

  const applyView = useCallback(async () => {
    const cached = loadLaborAnalyticsCacheEntry(rangeCacheQuery)
    if (cached) {
      applyViewGenerationRef.current += 1
      setChartsRefreshing(false)
      applyCachedEntry(cached)
      return
    }
    const generation = ++applyViewGenerationRef.current
    const hadCharts = chartsByStoreRef.current != null
    if (hadCharts) {
      setChartsRefreshing(true)
    }
    try {
      const payload = await fetchLaborRefresh(fetchQuery)
      if (generation !== applyViewGenerationRef.current) return
      const saved = saveLaborAnalyticsCache({
        rangePreset,
        query: { ...rangeCacheQuery, timescale },
        payload,
      })
      applyCachedEntry(saved)
    } catch (err) {
      if (generation !== applyViewGenerationRef.current) return
      toast.error(formatApiError(err, 'Failed to apply labor filters'))
    } finally {
      if (generation === applyViewGenerationRef.current) {
        setChartsRefreshing(false)
      }
    }
  }, [applyCachedEntry, fetchQuery, rangeCacheQuery, rangePreset, timescale])

  useEffect(() => {
    const exact = loadLaborAnalyticsCacheEntry(rangeCacheQuery)
    if (exact) {
      applyCachedEntry(exact)
      setPulledRange({ start: startDate, end: endDate })
      setHasRunQuery(true)
      return
    }
    if (hasLaborAnalyticsRangeCache(startDate, endDate)) {
      setPulledRange({ start: startDate, end: endDate })
      return
    }
    setPulledRange(null)
    resetToIdle()
    // Timescale is a view filter on an already-pulled range; only the date range
    // should drop back to idle and hide those controls.
  }, [applyCachedEntry, endDate, rangeCacheQuery, resetToIdle, startDate])

  useEffect(() => {
    if (!hasPulledCurrentRange) return
    const cached = loadLaborAnalyticsCacheEntry(rangeCacheQuery)
    if (cached) {
      applyViewGenerationRef.current += 1
      setChartsRefreshing(false)
      applyCachedEntry(cached)
      return
    }
    void applyView()
  }, [applyCachedEntry, applyView, hasPulledCurrentRange, rangeCacheQuery])

  useEffect(() => {
    if (!hasPulledCurrentRange) return
    const cached = loadLaborAnalyticsCacheEntry(rangeCacheQuery)
    if (cached) {
      applyCachedEntry(cached)
    }
  }, [applyCachedEntry, hasPulledCurrentRange, rangeCacheQuery, timescale])

  useEffect(() => {
    if (!hasPulledCurrentRange) return
    if (chartsByStore) return

    const cached = loadLaborAnalyticsCacheEntry(rangeCacheQuery)
    if (cached?.payload.charts_by_store) {
      applyCachedEntry(cached)
      return
    }

    void (async () => {
      try {
        const payload = await fetchLaborRefresh(fetchQuery)
        const saved = saveLaborAnalyticsCache({
          rangePreset,
          query: { ...rangeCacheQuery, timescale },
          payload,
        })
        applyCachedEntry(saved)
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load store compare charts'))
      }
    })()
  }, [
    applyCachedEntry,
    chartsByStore,
    fetchQuery,
    hasPulledCurrentRange,
    rangeCacheQuery,
    rangePreset,
    timescale,
  ])

  const applyPreset = useCallback((preset: LaborRangePreset) => {
    setRangePreset(preset)
    if (preset === 'custom') return
    const next = laborRangeForPreset(preset)
    setStartDate(next.start)
    setEndDate(next.end)
  }, [])

  async function handleRefresh(options?: { clearCache?: boolean }) {
    const query = fetchQuery
    const cacheQuery = { ...rangeCacheQuery, timescale }
    const loading = <T,>(): SectionLoadState<T> => ({ status: 'loading' })
    applyViewGenerationRef.current += 1
    if (options?.clearCache) {
      clearLaborAnalyticsCache()
    }
    setChartsRefreshing(false)
    setRefreshing(true)
    setCachedAt(null)
    setIncompleteRoles([])
    setGrossSalesPerHour(loading())
    setRegularHours(loading())
    setOvertimeHours(loading())
    setWagesByStation(loading())
    setWagesPctOfStationSales(loading())
    setAvgHourlyWages(loading())
    setWagesPctOfSales(loading())
    setOtEmployees(loading())
    setOtStations(loading())

    try {
      const payload = await fetchLaborRefresh({
        ...query,
        force: Boolean(options?.clearCache),
      })
      const saved = saveLaborAnalyticsCache({
        rangePreset,
        query: cacheQuery,
        payload,
      })
      applyCachedEntry(saved)
      setPulledRange({ start: startDate, end: endDate })
      setHasRunQuery(true)
    } catch (err) {
      const message = formatApiError(err, 'Failed to refresh labor charts')
      const failed = <T,>(): SectionLoadState<T> => ({ status: 'error', message })
      setGrossSalesPerHour(failed())
      setRegularHours(failed())
      setOvertimeHours(failed())
      setWagesByStation(failed())
      setWagesPctOfStationSales(failed())
      setAvgHourlyWages(failed())
      setWagesPctOfSales(failed())
      setOtEmployees(failed())
      setOtStations(failed())
      setPulledRange(null)
    } finally {
      setRefreshing(false)
    }
  }

  const exportDisabled = !hasPulledCurrentRange || refreshing || isExporting

  async function runLaborExport(
    deliver: (matrix: { headers: string[]; rows: Array<Array<string | number>> }) => void | Promise<void>,
  ) {
    if (exportDisabled) return
    setIsExporting(true)
    try {
      const payload = await collectLaborExportPayloads(startDate, endDate, {
        rangePreset,
      })
      if (!payload.charts_by_store) {
        throw new Error('No labor analytics data available to export')
      }
      const matrix = buildLaborAnalyticsExportMatrix(payload)
      await deliver(matrix)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to export labor analytics'))
    } finally {
      setIsExporting(false)
    }
  }

  function exportLaborCsv() {
    void runLaborExport((matrix) => {
      const csvRows = matrix.rows.map((row) =>
        row.map((cell) => escapeCsvCell(String(cell))).join(','),
      )
      downloadCsvFile(
        buildLaborAnalyticsExportFilename(startDate, endDate),
        [matrix.headers.join(','), ...csvRows].join('\n'),
      )
      toast.success('Exported labor analytics CSV')
    })
  }

  async function exportLaborToGoogleSheet() {
    await runLaborExport(async (matrix) => {
      const result = await exportLaborAnalyticsSheet({
        headers: matrix.headers,
        rows: matrix.rows,
      })
      toast.success(`Created Google Sheet: ${result.name}`, {
        persistent: true,
        action: {
          label: 'Open Sheet',
          onClick: () => {
            window.open(result.url, '_blank', 'noopener,noreferrer')
          },
        },
      })
      window.open(result.url, '_blank', 'noopener,noreferrer')
    })
  }

  const queryButtonLabel = laborQueryButtonLabel(refreshing)
  const queryCacheMenu = laborQueryShowsCacheMenu(
    cachedAt != null || hasLaborAnalyticsRangeCache(startDate, endDate),
  )

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="shrink-0 space-y-2">
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="labor-analytics-filters flex flex-wrap items-end gap-4">
          <label className="text-sm text-gray-700">
            Time range
            <select
              className="input mt-1 block text-sm"
              value={rangePreset}
              onChange={(event) => applyPreset(event.target.value as LaborRangePreset)}
            >
              {RANGE_PRESETS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-gray-700">
            Start
            <DateInput
              value={startDate}
              onChange={(value) => {
                setRangePreset('custom')
                setStartDate(value)
              }}
              dateFormat="iso"
              className="input mt-1 text-sm"
            />
          </label>

          <label className="text-sm text-gray-700">
            End
            <DateInput
              value={endDate}
              onChange={(value) => {
                setRangePreset('custom')
                setEndDate(value)
              }}
              dateFormat="iso"
              className="input mt-1 text-sm"
            />
          </label>

          {hasPulledCurrentRange ? (
            <>
              <div className="w-52">
                <MultiSelectFilter
                  id="labor-analytics-station"
                  label="Station"
                  options={stationOptions}
                  selected={stations}
                  onChange={setStations}
                  placeholder="All"
                />
              </div>

              <label className="text-sm text-gray-700">
                Timescale
                <select
                  className="input mt-1 block text-sm"
                  value={timescale}
                  onChange={(event) => setTimescale(event.target.value as LaborTimescale)}
                >
                  {LABOR_TIMESCALES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <LaborQueryButton
              label={queryButtonLabel}
              disabled={refreshing}
              cachedTitle={cachedAt != null ? `Cached ${formatCachedAt(cachedAt)}` : undefined}
              showCacheMenu={queryCacheMenu}
              onRunQuery={() => void handleRefresh()}
              onClearCacheAndRun={() => void handleRefresh({ clearCache: true })}
            />
            {hasRunQuery ? (
              <TableToolbar
                exportOptions={[
                  {
                    id: 'sheets',
                    label: 'Google Sheet',
                    onClick: () => void exportLaborToGoogleSheet(),
                    disabled: exportDisabled,
                  },
                  {
                    id: 'csv',
                    label: 'CSV',
                    onClick: exportLaborCsv,
                    disabled: exportDisabled,
                  },
                ]}
                defaultExportId="sheets"
                isExporting={isExporting}
                exportDisabled={exportDisabled}
              />
            ) : null}
          </div>
        </div>
      </div>

      </div>

      {incompleteRoles.length > 0 ? (
        <WarningBanner message={incompleteRolesWarningMessage(incompleteRoles)} />
      ) : null}

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-6">
      <div className="grid grid-cols-1 gap-5">
        <SectionCard subtitleVariant="relaxed"
          title="Gross Sales per Total Hour"
          fillBody
          subtitle={laborChartCaption('gross_sales_per_hour')}
        >
          <SectionBody
            state={grossSalesPerHour}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => (
                <LaborStoreCompare
                  loading={chartsRefreshing}
                  seriesByStore={storeMetricSeries(filteredChartsByStore, 'gross_sales_per_hour')}
                  closedDatesByStore={closedDatesByStore}
                  ariaLabel="Gross sales per total hour"
                  valueFormatter={formatUsd}
                  yAxisFormatter={formatAxisUsd}
                  bucketSeries={false}
                  pctTooltip={{
                    numeratorLabel: 'Gross Sales',
                    denominatorLabel: 'Total Hours',
                    formatNumerator: formatUsd,
                    formatDenominator: formatHours,
                    formatRatio: formatUsd,
                  }}
                />
              ))
            }
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Regular Hours by Station"
          fillBody
          subtitle={laborChartCaption('regular_hours_by_station', regularHoursMode)}
          headerRight={
            <SegmentedToggle
              ariaLabel="Regular hours display mode"
              options={[
                { value: 'abs', label: 'Abs' },
                { value: 'pct', label: 'Pct' },
              ]}
              value={regularHoursMode}
              onChange={setRegularHoursMode}
            />
          }
        >
          <SectionBody
            state={regularHours}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => {
                const seriesByStore = Object.fromEntries(
                  LABOR_STORE_NAMES.map((store) => {
                    const raw = storeMetricSeries(filteredChartsByStore, 'regular_hours_by_station')[store]
                    const series =
                      regularHoursMode === 'pct' ? convertHoursToPercentSeries(raw) : raw
                    return [store, series]
                  }),
                ) as Record<LaborStoreName, LaborSeries[]>
                return (
                  <LaborStoreCompare
                    loading={chartsRefreshing}
                    seriesByStore={seriesByStore}
                    closedDatesByStore={closedDatesByStore}
                    ariaLabel="Regular hours by station"
                    valueFormatter={
                      regularHoursMode === 'pct' ? formatHoursTooltipPct : formatHoursTooltip
                    }
                    yAxisFormatter={
                      regularHoursMode === 'pct' ? formatHoursAxisPct : formatAxisHours
                    }
                    bucketSeries={false}
                    pctTooltip={
                      regularHoursMode === 'pct'
                        ? {
                            numeratorLabel: 'Station',
                            denominatorLabel: 'Total',
                            formatNumerator: formatHoursTooltip,
                            formatDenominator: formatHoursTooltip,
                            formatRatio: formatHoursTooltipPct,
                          }
                        : undefined
                    }
                  />
                )
              })
            }
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Overtime Hours by Station"
          fillBody
          subtitle={laborChartCaption('overtime_hours_by_station', overtimeHoursMode)}
          headerRight={
            <SegmentedToggle
              ariaLabel="Overtime hours display mode"
              options={[
                { value: 'abs', label: 'Abs' },
                { value: 'pct', label: 'Pct' },
              ]}
              value={overtimeHoursMode}
              onChange={setOvertimeHoursMode}
            />
          }
        >
          <SectionBody
            state={overtimeHours}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => {
                const seriesByStore = Object.fromEntries(
                  LABOR_STORE_NAMES.map((store) => {
                    const raw = storeMetricSeries(filteredChartsByStore, 'overtime_hours_by_station')[store]
                    const series =
                      overtimeHoursMode === 'pct' ? convertHoursToPercentSeries(raw) : raw
                    return [store, series]
                  }),
                ) as Record<LaborStoreName, LaborSeries[]>
                return (
                  <LaborStoreCompare
                    loading={chartsRefreshing}
                    seriesByStore={seriesByStore}
                    closedDatesByStore={closedDatesByStore}
                    ariaLabel="Overtime hours by station"
                    valueFormatter={
                      overtimeHoursMode === 'pct' ? formatHoursTooltipPct : formatHoursTooltip
                    }
                    yAxisFormatter={
                      overtimeHoursMode === 'pct' ? formatHoursAxisPct : formatAxisHours
                    }
                    bucketSeries={false}
                    pctTooltip={
                      overtimeHoursMode === 'pct'
                        ? {
                            numeratorLabel: 'Station',
                            denominatorLabel: 'Total',
                            formatNumerator: formatHoursTooltip,
                            formatDenominator: formatHoursTooltip,
                            formatRatio: formatHoursTooltipPct,
                          }
                        : undefined
                    }
                  />
                )
              })
            }
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Total Wages by Station"
          fillBody
          subtitle={
            wagesMode === 'pct' ? (
              <>
                {laborChartCaption('wages_by_station', 'pct')}
                <span className="mt-1.5 block">{WAGES_STATION_SALES_CAVEAT}</span>
              </>
            ) : (
              laborChartCaption('wages_by_station', 'usd')
            )
          }
          headerRight={
            <SegmentedToggle
              ariaLabel="Wages display mode"
              options={[
                { value: 'usd', label: '$' },
                { value: 'pct', label: 'Pct' },
              ]}
              value={wagesMode}
              onChange={setWagesMode}
            />
          }
        >
          <SectionBody
            state={wagesMode === 'pct' ? wagesPctOfStationSales : wagesByStation}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => {
                const seriesByStore = Object.fromEntries(
                  LABOR_STORE_NAMES.map((store) => {
                    const abs = storeMetricSeries(filteredChartsByStore, 'wages_by_station')[store]
                    const pct = storeMetricSeries(filteredChartsByStore, 'wages_pct_of_station_sales')[store]
                    const series =
                      wagesMode === 'pct'
                        ? withoutTotalSeries(withRatioNumeratorDenominator(pct, abs))
                        : abs
                    return [store, series]
                  }),
                ) as Record<LaborStoreName, LaborSeries[]>
                return (
                  <LaborStoreCompare
                    loading={chartsRefreshing}
                    seriesByStore={seriesByStore}
                    closedDatesByStore={closedDatesByStore}
                    ariaLabel="Total wages by station"
                    valueFormatter={wagesMode === 'pct' ? formatLaborTooltipPct : formatUsd}
                    yAxisFormatter={wagesMode === 'pct' ? formatLaborAxisPct : formatAxisUsd}
                    bucketSeries={false}
                    pctTooltip={
                      wagesMode === 'pct'
                        ? {
                            numeratorLabel: 'Wages',
                            denominatorLabel: 'Sales',
                            formatNumerator: formatUsd,
                            formatDenominator: formatUsd,
                            formatRatio: formatLaborTooltipPct,
                          }
                        : undefined
                    }
                  />
                )
              })
            }
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Average Hourly Wages"
          fillBody
          subtitle={laborChartCaption('avg_hourly_wages')}
        >
          <SectionBody
            state={avgHourlyWages}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => (
                <LaborStoreCompare
                  loading={chartsRefreshing}
                  seriesByStore={storeMetricSeries(filteredChartsByStore, 'avg_hourly_wages')}
                  closedDatesByStore={closedDatesByStore}
                  ariaLabel="Average hourly wages"
                  valueFormatter={formatUsd}
                  yAxisFormatter={formatAxisUsd}
                  bucketSeries={false}
                />
              ))
            }
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Total Wages as % of Gross Sales"
          fillBody
          subtitle={laborChartCaption('wages_pct_of_sales')}
        >
          <SectionBody
            state={wagesPctOfSales}
            fill
            render={() =>
              compareLoadingOr(filteredChartsByStore, () => (
                <LaborStoreCompare
                  loading={chartsRefreshing}
                  seriesByStore={storeMetricSeries(filteredChartsByStore, 'wages_pct_of_sales')}
                  closedDatesByStore={closedDatesByStore}
                  ariaLabel="Total wages as percent of gross sales"
                  valueFormatter={formatLaborTooltipPct}
                  yAxisFormatter={formatLaborAxisPct}
                  bucketSeries={false}
                />
              ))
            }
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 items-start gap-5">
        <SectionCard subtitleVariant="relaxed"
          title="Top OT by Employee"
          subtitle="Timescale does not apply to weekly tables; data shown on a weekly basis"
          headerRight={
            <OtWeekSelect
              weeks={otWeeks}
              value={selectedOtWeek}
              onChange={setSelectedOtWeek}
              rangeStart={startDate}
              rangeEnd={endDate}
            />
          }
        >
          <SectionBody
            state={otEmployees}
            render={(data) => (
              <WeeklyOtEmployeeTable data={data} week={selectedOtWeek} storeFilter={[]} />
            )}
          />
        </SectionCard>

        <SectionCard subtitleVariant="relaxed"
          title="Top OT by Station"
          subtitle="Timescale does not apply to weekly tables; data shown on a weekly basis"
          headerRight={
            <OtWeekSelect
              weeks={otWeeks}
              value={selectedOtWeek}
              onChange={setSelectedOtWeek}
              rangeStart={startDate}
              rangeEnd={endDate}
            />
          }
        >
          <SectionBody
            state={otStations}
            render={(data) => (
              <WeeklyOtStationTable data={data} week={selectedOtWeek} storeFilter={[]} stationFilter={effectiveStationFilter} />
            )}
          />
        </SectionCard>
      </div>
      </div>
    </div>
  )
}
